// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit2, Copy, ExternalLink, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

interface ReferralLinkWithAffiliate {
  id: string;
  name: string;
  url: string;
  code: string | null;
  description: string | null;
  clicks: number;
  conversions: number;
  is_active: boolean;
  created_at: string;
  affiliate_id: string;
  affiliate_name: string;
  affiliate_email: string;
}

interface Affiliate {
  user_id: string;
  name: string;
  email: string;
}

interface ReferralLinkFormData {
  name: string;
  url: string;
  code: string;
  description: string;
  affiliate_id: string;
}

const AdminReferralLinksTab = () => {
  const [links, setLinks] = useState<ReferralLinkWithAffiliate[]>([]);
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<ReferralLinkWithAffiliate | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<ReferralLinkFormData>({
    name: '',
    url: '',
    code: '',
    description: '',
    affiliate_id: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch referral links with affiliate info
      const { data: linksData, error: linksError } = await supabase
        .from('affiliate_referral_links')
        .select(`
          *,
          profiles!affiliate_referral_links_affiliate_id_fkey (
            name,
            email
          )
        `)
        .order('created_at', { ascending: false });

      if (linksError) {
        console.error('Error fetching referral links:', linksError);
        toast.error('Failed to load referral links');
      } else {
        const formattedLinks = linksData?.map((link: any) => ({
          id: link.id,
          name: link.name,
          url: link.url,
          code: link.code,
          description: link.description,
          clicks: link.clicks,
          conversions: link.conversions,
          is_active: link.is_active,
          created_at: link.created_at,
          affiliate_id: link.affiliate_id,
          affiliate_name: link.profiles?.name || 'Unknown',
          affiliate_email: link.profiles?.email || 'Unknown'
        })) || [];
        setLinks(formattedLinks);
      }

      // Fetch affiliates for the dropdown
      const { data: affiliatesData, error: affiliatesError } = await supabase
        .from('affiliates')
        .select(`
          user_id,
          profiles!affiliates_user_id_fkey (
            name,
            email
          )
        `)
        .eq('status', 'active');

      if (affiliatesError) {
        console.error('Error fetching affiliates:', affiliatesError);
      } else {
        const formattedAffiliates = affiliatesData?.map((affiliate: any) => ({
          user_id: affiliate.user_id,
          name: affiliate.profiles?.name || 'Unknown',
          email: affiliate.profiles?.email || 'Unknown'
        })) || [];
        setAffiliates(formattedAffiliates);
      }
    } catch (error) {
      console.error('Error in fetchData:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredLinks = links.filter(link => 
    link.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    link.affiliate_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    link.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    link.affiliate_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCreateModal = () => {
    setEditingLink(null);
    setFormData({
      name: '',
      url: '',
      code: '',
      description: '',
      affiliate_id: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (link: ReferralLinkWithAffiliate) => {
    setEditingLink(link);
    setFormData({
      name: link.name,
      url: link.url,
      code: link.code || '',
      description: link.description || '',
      affiliate_id: link.affiliate_id
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingLink(null);
    setFormData({
      name: '',
      url: '',
      code: '',
      description: '',
      affiliate_id: ''
    });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Link name is required');
      return false;
    }
    
    if (!formData.url.trim()) {
      toast.error('URL is required');
      return false;
    }
    
    if (!formData.code.trim()) {
      toast.error('Code is required');
      return false;
    }

    if (!formData.affiliate_id) {
      toast.error('Please select an affiliate');
      return false;
    }

    // Validate code format (alphanumeric, no spaces)
    const codeRegex = /^[A-Za-z0-9]+$/;
    if (!codeRegex.test(formData.code)) {
      toast.error('Code must contain only letters and numbers (no spaces or special characters)');
      return false;
    }

    try {
      new URL(formData.url);
    } catch {
      toast.error('Please enter a valid URL');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    
    try {
      const linkData = {
        affiliate_id: formData.affiliate_id,
        name: formData.name.trim(),
        url: formData.url.trim(),
        code: formData.code.trim().toUpperCase(),
        description: formData.description.trim() || null,
        is_active: true
      };

      if (editingLink) {
        // Update existing link
        const { error } = await supabase
          .from('affiliate_referral_links')
          .update(linkData)
          .eq('id', editingLink.id);

        if (error) {
          if (error.code === '23505') { // Unique constraint violation
            toast.error('This code is already in use. Please choose a different code.');
            return;
          }
          throw error;
        }

        toast.success('Referral link updated successfully');
      } else {
        // Create new link
        const { error } = await supabase
          .from('affiliate_referral_links')
          .insert([linkData]);

        if (error) {
          if (error.code === '23505') { // Unique constraint violation
            toast.error('This code is already in use. Please choose a different code.');
            return;
          }
          throw error;
        }

        toast.success('Referral link created successfully');
      }

      closeModal();
      fetchData();
    } catch (error) {
      console.error('Error saving referral link:', error);
      toast.error('Failed to save referral link');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = (url: string, name: string) => {
    navigator.clipboard.writeText(url);
    toast.success(`${name} copied to clipboard!`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-600">Loading referral links...</span>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Affiliate Referral Links</CardTitle>
              <CardDescription>
                Manage all affiliate referral links and their unique codes
              </CardDescription>
            </div>
            <Button onClick={openCreateModal} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              New Link
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by name, affiliate, code, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {filteredLinks.length === 0 ? (
            <div className="text-center py-8">
              <ExternalLink className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'No matching links found' : 'No Referral Links Yet'}
              </h3>
              <p className="text-gray-600 mb-4">
                {searchTerm ? 'Try adjusting your search terms.' : 'Create the first referral link to get started.'}
              </p>
              {!searchTerm && (
                <Button onClick={openCreateModal} className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Create First Link
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Name</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Affiliate</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Code</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">URL</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Performance</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLinks.map((link) => (
                    <tr key={link.id} className="border-b border-gray-100">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-sm">{link.name}</div>
                          {link.description && (
                            <div className="text-xs text-gray-500">{link.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-sm">{link.affiliate_name}</div>
                          <div className="text-xs text-gray-500">{link.affiliate_email}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                          {link.code || 'No code'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="max-w-xs truncate text-sm">{link.url}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          <div>{link.clicks} clicks</div>
                          <div>{link.conversions} conversions</div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopyLink(link.url, link.name)}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditModal(link)}
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingLink ? 'Edit Referral Link' : 'Create New Referral Link'}
            </DialogTitle>
            <DialogDescription>
              {editingLink ? 'Update the referral link details' : 'Create a new referral link with a unique code'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="affiliate">Affiliate</Label>
              <Select
                value={formData.affiliate_id}
                onValueChange={(value) => setFormData({ ...formData, affiliate_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an affiliate" />
                </SelectTrigger>
                <SelectContent>
                  {affiliates.map((affiliate) => (
                    <SelectItem key={affiliate.user_id} value={affiliate.user_id}>
                      {affiliate.name} ({affiliate.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="name">Link Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Summer Campaign"
              />
            </div>

            <div>
              <Label htmlFor="code">Unique Code</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., AFF1234"
                className="font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use only letters and numbers. This code must be unique across all affiliates.
              </p>
            </div>

            <div>
              <Label htmlFor="url">Target URL</Label>
              <Input
                id="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://example.com/landing-page"
                type="url"
              />
            </div>

            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe this referral link..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {editingLink ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editingLink ? 'Update Link' : 'Create Link'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminReferralLinksTab;
